import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  MagnifyingGlassIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  TicketIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { getFirestore, collection, doc, onSnapshot } from 'firebase/firestore';
import { getAdminRevenue } from '../firebase/adminService';
import {
  createVoucherCode,
  deleteVoucherCode,
  generateCode,
  setVoucherActive,
  subscribeToVoucherCodes,
  voucherStatus,
} from '../firebase/voucherService';
import { logout } from '../firebase/authService';
import { useError } from '../context/ErrorContext';
import {
  BrandInput,
  BrandLabel,
  BrandSpinner,
  BrandButton,
  chipClasses,
  PageHeading,
  PageShell,
  PANEL,
  PANEL_HOVER,
  PanelTitle,
  RING_SETS,
  SegmentedRule,
  WELL,
  brandButton,
} from '../components/ui/Brand';

// Foursquare's free monthly allowance for PRO endpoints. Small enough to be the
// real constraint: the district refresh alone spends ~330/month, so this bar
// genuinely matters rather than being decorative.
const FOURSQUARE_FREE_CALLS = 500;

// PREMIUM calls (the district refresh, once it can request photos) have no free
// allowance and bill from purchased credits at $10/10 000 — so they must not be
// charged against the 500 above, or both numbers read wrong. Counted separately
// via apiUsage.premiumCalls; Pro-billed is total − premium.
const PREMIUM_NOTE =
  'Premium calls bill from credits ($10 / 10 000) and do not use the free Pro allowance.';
const CURRENT_MONTH = new Date().toISOString().slice(0, 7); // YYYY-MM

const fmtNum = (n) => (n ?? 0).toLocaleString();

const fmtTs = (t) => {
  if (!t) return '—';
  const d = t.toDate ? t.toDate() : new Date(t);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const rand = (cents) =>
  `R${Math.round((cents || 0) / 100).toLocaleString('en-ZA')}`;

// --- Shared presentational pieces ---

function StatCard({ label, value, hint }) {
  return (
    <div className={`${PANEL} ${PANEL_HOVER}`}>
      <p className="font-mono text-xs font-bold uppercase tracking-wider text-white/50">
        {label}
      </p>
      <p className="mt-3 font-mono text-4xl font-bold leading-none text-white">
        {value}
      </p>
      {hint && <p className="mt-2 font-mono text-xs text-white/50">{hint}</p>}
    </div>
  );
}

function TierPill({ tier }) {
  const tones = {
    trial: 'neutral',
    starter: 'cool',
    pro: 'danger',
    enterprise: 'success',
  };
  const label = tier || 'trial';
  return (
    <span className={chipClasses(tones[label] || 'neutral', 'capitalize')}>
      {label}
    </span>
  );
}

// Table styling is shared by every directory/report table on this page.
const TH = 'pb-3 font-mono text-xs font-bold uppercase tracking-wider text-white/45';
const TR = 'border-t border-white/10';
const TD = 'py-3 pr-4 font-mono text-sm text-white/85';
const TD_MUTED = 'py-3 pr-4 font-mono text-sm text-white/55';

// --- Overview tab ---

function OverviewTab({ stats, usage, districtCache, indexUpdatedAt }) {
  // Only Pro-billed calls draw on the free allowance. That is every call except
  // the Premium ones, NOT just owner lookups — while the org has no credits the
  // district refresh runs at Pro rate too and does consume the 500.
  const proCalls = Math.max(usage.total - usage.premium, 0);
  const usagePct = Math.min((proCalls / FOURSQUARE_FREE_CALLS) * 100, 100);
  const over = proCalls > FOURSQUARE_FREE_CALLS;
  // Worth a visual warning well before the cliff: at ~330/month from the
  // refresh alone, "fine" and "over" are only a few owner lookups apart.
  const nearLimit = !over && proCalls > FOURSQUARE_FREE_CALLS * 0.8;

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-5">
        <StatCard
          label="Live Venues"
          value={fmtNum(stats.live)}
          hint={`${fmtNum(stats.total)} total`}
        />
        <StatCard
          label="Subscribed"
          value={fmtNum(stats.subscribed)}
          hint="Paid tiers"
        />
        <StatCard label="Owners" value={fmtNum(stats.owners)} />
        <StatCard
          label="Active Districts"
          value={fmtNum(stats.activeDistricts)}
          hint={`${fmtNum(stats.totalDistricts)} configured`}
        />
        <StatCard
          label="Venues Linked"
          value={fmtNum(stats.withPlaceId)}
          hint="Carry a Foursquare ID"
        />
      </div>

      <div className={PANEL}>
        <PanelTitle
          title="Foursquare API Usage"
          actions={
            <span className="font-mono text-sm text-white/50">
              {CURRENT_MONTH}
            </span>
          }
        />
        <p className="mt-4 font-mono text-3xl font-bold text-white">
          {fmtNum(proCalls)}
          <span className="text-base font-normal text-white/50">
            {' '}
            / {fmtNum(FOURSQUARE_FREE_CALLS)} free Pro calls
          </span>
        </p>
        <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-white/10">
          <div
            className={`h-full rounded-full ${
              over
                ? 'bg-brand-pink'
                : nearLimit
                  ? 'bg-brand-orange'
                  : 'bg-brand-cool'
            }`}
            style={{ width: `${usagePct}%` }}
          />
        </div>
        <div className="mt-4 flex flex-col gap-1 font-mono text-sm text-white/60">
          <div className="flex flex-wrap gap-x-8 gap-y-1">
            <span>Scheduled refresh: {fmtNum(usage.refresh)}</span>
            <span>Owner lookups: {fmtNum(usage.search)}</span>
            <span data-testid="usage-premium">
              Premium (credits): {fmtNum(usage.premium)}
            </span>
          </div>
          <p className="text-xs text-white/45">{PREMIUM_NOTE}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className={PANEL}>
          <PanelTitle title="Venues by Tier" />
          <ul className="mt-4 flex flex-col">
            {['trial', 'starter', 'pro', 'enterprise'].map((tier) => (
              <li
                key={tier}
                className="flex items-center justify-between border-b border-white/10 py-2.5 font-mono text-sm capitalize last:border-0"
              >
                <span className="text-white/60">{tier}</span>
                <span className="font-bold text-white">
                  {fmtNum(stats.byTier[tier])}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className={PANEL}>
          <PanelTitle
            title="District Cache"
            actions={
              <span className="font-mono text-sm text-white/50">
                Index: {fmtTs(indexUpdatedAt)}
              </span>
            }
          />
          {districtCache.length === 0 ? (
            <p className="mt-4 font-mono text-sm text-white/50">
              No district snapshots yet — seed districts and run the refresh.
            </p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr>
                    <th className={TH}>District</th>
                    <th className={`${TH} text-right`}>Venues</th>
                    <th className={`${TH} text-right`}>Refreshed</th>
                  </tr>
                </thead>
                <tbody>
                  {districtCache.map((d) => (
                    <tr key={d.id} className={TR}>
                      <td className={TD}>{d.name}</td>
                      <td className={`${TD_MUTED} text-right`}>
                        {fmtNum(d.venueCount)}
                      </td>
                      <td className={`${TD_MUTED} text-right`}>
                        {fmtTs(d.fetchedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Venues & Owners tab (searchable) ---

function DirectoryTab({ venues, users }) {
  const [mode, setMode] = useState('venues');
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  const filteredVenues = useMemo(() => {
    if (!q) return venues;
    return venues.filter((v) =>
      [v.name, v.address, v.placeId, v.ownerId, v.id].some((f) =>
        (f || '').toLowerCase().includes(q)
      )
    );
  }, [venues, q]);

  const filteredOwners = useMemo(() => {
    if (!q) return users;
    return users.filter((u) =>
      [u.email, u.displayName, u.firstName, u.lastName].some((f) =>
        (f || '').toLowerCase().includes(q)
      )
    );
  }, [users, q]);

  const rows = mode === 'venues' ? filteredVenues : filteredOwners;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-white/20 bg-white/[0.06] p-1">
          {['venues', 'owners'].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded-md px-4 py-1.5 font-display text-sm font-bold capitalize transition ${
                mode === m
                  ? 'bg-brand-warm text-white'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
        <div className="relative min-w-[16rem] flex-1">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40"
            aria-hidden="true"
          />
          <BrandInput
            type="search"
            className="pl-9"
            placeholder={`Search ${mode}…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <span className="font-mono text-sm text-white/50">
          {rows.length} {mode}
        </span>
      </div>

      <div className={`${PANEL} overflow-x-auto`}>
        {mode === 'venues' ? (
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className={TH}>Venue</th>
                <th className={TH}>Status</th>
                <th className={TH}>Tier</th>
                <th className={TH}>Foursquare</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id} className={`${TR} align-top`}>
                  <td className={TD}>
                    <p className="font-bold text-white">
                      {v.name || '(unnamed)'}
                    </p>
                    <p className="text-xs text-white/50">{v.address}</p>
                  </td>
                  <td className={TD}>
                    <span
                      className={
                        v.published ? 'text-brand-green' : 'text-white/50'
                      }
                    >
                      {v.published ? 'Live' : 'Draft'}
                    </span>
                  </td>
                  <td className={TD}>
                    <TierPill tier={v.subscriptionTier} />
                  </td>
                  <td className={`${TD_MUTED} text-xs`}>
                    {v.placeId ? v.placeId : '— none —'}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="py-6 text-center font-mono text-sm text-white/50"
                  >
                    No matching venues.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className={TH}>Owner</th>
                <th className={TH}>Email</th>
                <th className={TH}>Tier</th>
                <th className={TH}>Verification</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className={TR}>
                  <td className={TD}>
                    {u.displayName ||
                      `${u.firstName || ''} ${u.lastName || ''}`.trim() ||
                      '(no name)'}
                  </td>
                  <td className={TD_MUTED}>{u.email}</td>
                  <td className={TD}>
                    <TierPill tier={u.subscriptionTier} />
                  </td>
                  <td className={TD_MUTED}>
                    {u.verificationStatus || 'UNVERIFIED'}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="py-6 text-center font-mono text-sm text-white/50"
                  >
                    No matching owners.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// --- Revenue tab (Paystack, on-demand) ---

function RevenueTab() {
  const { showError } = useError();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const result = await getAdminRevenue();
        if (alive) setData(result);
      } catch (error) {
        showError('Failed to load Paystack revenue figures.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [showError]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <BrandSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-5">
        <StatCard
          label="MRR"
          value={rand(data.mrrCents)}
          hint="Monthly recurring (est.)"
        />
        <StatCard
          label="Active Subscriptions"
          value={fmtNum(data.activeSubscriptions)}
        />
        <StatCard
          label="Revenue This Month"
          value={rand(data.thisMonth.volumeCents)}
          hint={`${fmtNum(data.thisMonth.count)} transactions`}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className={PANEL}>
          <PanelTitle title="Active Subscriptions by Tier" />
          <ul className="mt-4 flex flex-col">
            {['starter', 'pro', 'enterprise'].map((tier) => (
              <li
                key={tier}
                className="flex items-center justify-between border-b border-white/10 py-2.5 font-mono text-sm capitalize last:border-0"
              >
                <span className="text-white/60">{tier}</span>
                <span className="font-bold text-white">
                  {fmtNum(data.byTier[tier])}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className={`${PANEL} overflow-x-auto`}>
          <PanelTitle title="Recent Charges" />
          {data.recentTransactions.length === 0 ? (
            <p className="mt-4 font-mono text-sm text-white/50">
              No successful charges yet.
            </p>
          ) : (
            <table className="mt-4 w-full text-left">
              <tbody>
                {data.recentTransactions.map((tx) => (
                  <tr key={tx.reference} className={TR}>
                    <td className={TD}>{tx.email}</td>
                    <td className={TD_MUTED}>{fmtTs(tx.paidAt)}</td>
                    <td className={`${TD} text-right font-bold`}>
                      {rand(tx.amountCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <p className="font-mono text-xs text-white/50">
        Paystack figures as of {fmtTs(data.generatedAt)}. Reopen this tab to
        refresh.
      </p>
    </div>
  );
}

// --- Vouchers tab (generate & retire Pro giveaway codes) ---

const BLANK_VOUCHER = {
  code: '',
  months: '1',
  maxRedemptions: '1',
  campaign: '',
  expires: '',
};

// Copy button with a transient confirmation — codes get pasted into DMs and
// story slides, and retyping one by hand is how a wrong code goes out.
function CopyCodeButton({ code }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      // Clipboard is permission-gated and unavailable over plain http; the
      // code is on screen either way, so a failure needs no error surface.
    }
  }, [code]);

  const Icon = copied ? ClipboardDocumentCheckIcon : ClipboardDocumentIcon;

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied' : `Copy ${code}`}
      aria-label={copied ? 'Copied' : `Copy ${code}`}
      data-testid={`copy-${code}`}
      className={`rounded p-1.5 transition ${
        copied
          ? 'text-brand-green'
          : 'text-white/45 hover:bg-white/10 hover:text-white'
      }`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}

function VouchersTab() {
  const { showError } = useError();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(BLANK_VOUCHER);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    const unsub = subscribeToVoucherCodes(
      (next) => {
        setCodes(next);
        setLoading(false);
      },
      () => {
        setLoading(false);
        showError('Could not load voucher codes. Check admin access.');
      }
    );
    return unsub;
  }, [showError]);

  const setField = useCallback(
    (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
    []
  );

  const handleGenerate = useCallback(() => {
    setForm((f) => ({ ...f, code: generateCode() }));
  }, []);

  const handleCreate = useCallback(
    async (e) => {
      e.preventDefault();
      setSaving(true);
      setCreated(null);
      try {
        // A date-only input means "redeemable through the end of that day",
        // so push the cutoff to the last moment of it rather than midnight,
        // which would expire the code a full day early.
        let expiresAt = null;
        if (form.expires) {
          const [y, m, d] = form.expires.split('-').map(Number);
          expiresAt = new Date(y, m - 1, d, 23, 59, 59, 999);
        }

        const code = await createVoucherCode({
          code: form.code,
          months: form.months,
          maxRedemptions: form.maxRedemptions,
          campaign: form.campaign,
          expiresAt,
        });
        setForm(BLANK_VOUCHER);
        setCreated(code);
      } catch (error) {
        showError(error.message || 'Could not create that voucher code.');
      } finally {
        setSaving(false);
      }
    },
    [form, showError]
  );

  const handleToggle = useCallback(
    async (voucher) => {
      try {
        await setVoucherActive(voucher.code, voucher.active === false);
      } catch {
        showError(`Could not update ${voucher.code}.`);
      }
    },
    [showError]
  );

  const handleDelete = useCallback(
    async (voucher) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete ${voucher.code}? This cannot be undone.`)) {
        return;
      }
      try {
        await deleteVoucherCode(voucher.code);
      } catch {
        showError(`Could not delete ${voucher.code}.`);
      }
    },
    [showError]
  );

  const totals = useMemo(() => {
    let active = 0;
    let redemptions = 0;
    let monthsGranted = 0;
    codes.forEach((c) => {
      if (voucherStatus(c).label === 'Active') active += 1;
      const used = c.redemptionCount ?? 0;
      redemptions += used;
      monthsGranted += used * (c.months ?? 1);
    });
    return { active, redemptions, monthsGranted };
  }, [codes]);

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-5">
        <StatCard
          label="Codes"
          value={fmtNum(codes.length)}
          hint={`${fmtNum(totals.active)} redeemable now`}
        />
        <StatCard label="Redemptions" value={fmtNum(totals.redemptions)} />
        <StatCard
          label="Pro Months Granted"
          value={fmtNum(totals.monthsGranted)}
          hint="Across all redemptions"
        />
      </div>

      <div className={PANEL}>
        <PanelTitle
          title="New Voucher Code"
          subtitle="Grants time-boxed consumer Pro on redemption in the mobile app. Codes are case- and dash-insensitive when redeemed."
        />

        <form onSubmit={handleCreate} className="mt-5 flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-2 sm:col-span-2">
              <BrandLabel htmlFor="voucher-code">Code</BrandLabel>
              <div className="flex gap-2">
                <BrandInput
                  id="voucher-code"
                  data-testid="voucher-code"
                  value={form.code}
                  onChange={setField('code')}
                  placeholder="e.g. LAUNCHWEEK"
                  autoComplete="off"
                  spellCheck="false"
                  className="uppercase"
                />
                <BrandButton
                  variant="outline"
                  size="sm"
                  onClick={handleGenerate}
                  data-testid="generate-code"
                  className="shrink-0"
                >
                  <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                  Generate
                </BrandButton>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <BrandLabel htmlFor="voucher-months">Months of Pro</BrandLabel>
              <BrandInput
                id="voucher-months"
                data-testid="voucher-months"
                type="number"
                min="1"
                step="1"
                value={form.months}
                onChange={setField('months')}
              />
            </div>

            <div className="flex flex-col gap-2">
              <BrandLabel htmlFor="voucher-max">Max Redemptions</BrandLabel>
              <BrandInput
                id="voucher-max"
                data-testid="voucher-max"
                type="number"
                min="1"
                step="1"
                value={form.maxRedemptions}
                onChange={setField('maxRedemptions')}
              />
            </div>

            <div className="flex flex-col gap-2 sm:col-span-2">
              <BrandLabel htmlFor="voucher-campaign">
                Campaign <span className="font-normal">(optional)</span>
              </BrandLabel>
              <BrandInput
                id="voucher-campaign"
                data-testid="voucher-campaign"
                value={form.campaign}
                onChange={setField('campaign')}
                placeholder="e.g. Instagram launch giveaway"
              />
            </div>

            <div className="flex flex-col gap-2 sm:col-span-2">
              <BrandLabel htmlFor="voucher-expires">
                Redeemable Until <span className="font-normal">(optional)</span>
              </BrandLabel>
              <BrandInput
                id="voucher-expires"
                data-testid="voucher-expires"
                type="date"
                value={form.expires}
                onChange={setField('expires')}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <BrandButton type="submit" disabled={saving} data-testid="create-voucher">
              <TicketIcon className="h-4 w-4" aria-hidden="true" />
              {saving ? 'Creating…' : 'Create Code'}
            </BrandButton>
            {created && (
              <p
                className="font-mono text-sm text-brand-green"
                data-testid="voucher-created"
              >
                {created} is live.
              </p>
            )}
          </div>
        </form>
      </div>

      <div className={`${PANEL} overflow-x-auto`}>
        <PanelTitle
          title="All Codes"
          actions={
            <span className="font-mono text-sm text-white/50">
              {codes.length} total
            </span>
          }
        />
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <BrandSpinner />
          </div>
        ) : codes.length === 0 ? (
          <p className={`mt-4 ${WELL} font-mono text-sm text-white/55`}>
            No voucher codes yet — create one above to start handing out Pro.
          </p>
        ) : (
          <table className="mt-4 w-full text-left" data-testid="voucher-table">
            <thead>
              <tr>
                <th className={TH}>Code</th>
                <th className={TH}>Grants</th>
                <th className={TH}>Redeemed</th>
                <th className={TH}>Campaign</th>
                <th className={TH}>Until</th>
                <th className={TH}>Status</th>
                <th className={`${TH} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((v) => {
                const status = voucherStatus(v);
                const used = v.redemptionCount ?? 0;
                const paused = v.active === false;
                return (
                  <tr key={v.code} className={TR}>
                    <td className={TD}>
                      <span className="inline-flex items-center gap-1">
                        <span className="font-bold text-white">{v.code}</span>
                        <CopyCodeButton code={v.code} />
                      </span>
                    </td>
                    <td className={TD_MUTED}>
                      {v.months ?? 1} {(v.months ?? 1) === 1 ? 'month' : 'months'}
                    </td>
                    <td className={TD}>
                      {fmtNum(used)}
                      <span className="text-white/45">
                        {' '}
                        / {fmtNum(v.maxRedemptions ?? 1)}
                      </span>
                    </td>
                    <td className={TD_MUTED}>{v.campaign || '—'}</td>
                    <td className={TD_MUTED}>
                      {v.expiresAt ? fmtTs(v.expiresAt) : 'No cutoff'}
                    </td>
                    <td className={TD}>
                      <span className={chipClasses(status.tone)}>
                        {status.label}
                      </span>
                    </td>
                    <td className={`${TD} text-right`}>
                      <span className="inline-flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => handleToggle(v)}
                          title={paused ? `Resume ${v.code}` : `Pause ${v.code}`}
                          aria-label={
                            paused ? `Resume ${v.code}` : `Pause ${v.code}`
                          }
                          data-testid={`toggle-${v.code}`}
                          className="rounded p-1.5 text-white/45 transition hover:bg-white/10 hover:text-white"
                        >
                          {paused ? (
                            <PlayCircleIcon
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          ) : (
                            <PauseCircleIcon
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          )}
                        </button>
                        {/* Deleting a redeemed code would erase the only record
                            of what the campaign gave away, and the Pro it
                            already granted keeps running regardless — so
                            retiring those is Pause, not Delete. */}
                        {used === 0 && (
                          <button
                            type="button"
                            onClick={() => handleDelete(v)}
                            title={`Delete ${v.code}`}
                            aria-label={`Delete ${v.code}`}
                            data-testid={`delete-${v.code}`}
                            className="rounded p-1.5 text-white/45 transition hover:bg-brand-pink/15 hover:text-brand-pink"
                          >
                            <TrashIcon className="h-5 w-5" aria-hidden="true" />
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// --- Page shell ---

const TABS = ['Overview', 'Venues & Owners', 'Revenue', 'Vouchers'];

// Tabs that render from the page-level Firestore listeners and so must wait on
// `ready`. Revenue and Vouchers each load their own data and show their own
// spinner, so gating them here would stall them behind an unrelated fetch.
const LIVE_DATA_TABS = ['Overview', 'Venues & Owners'];

function AdminDashboard() {
  const navigate = useNavigate();
  const { showError } = useError();
  const [tab, setTab] = useState('Overview');

  const [venues, setVenues] = useState([]);
  const [users, setUsers] = useState([]);
  const [districtDocs, setDistrictDocs] = useState([]);
  const [cacheDocs, setCacheDocs] = useState([]);
  const [indexDoc, setIndexDoc] = useState(null);
  const [usageDoc, setUsageDoc] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const db = getFirestore();
    const onErr = () =>
      showError('Live admin data failed to load. Check admin access.');
    const map = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    const unsubs = [
      onSnapshot(
        collection(db, 'venues'),
        (s) => {
          setVenues(map(s));
          setReady(true);
        },
        onErr
      ),
      onSnapshot(collection(db, 'users'), (s) => setUsers(map(s)), onErr),
      onSnapshot(
        collection(db, 'districts'),
        (s) => setDistrictDocs(map(s)),
        onErr
      ),
      onSnapshot(
        collection(db, 'districtVenues'),
        (s) => setCacheDocs(map(s)),
        onErr
      ),
      onSnapshot(
        doc(db, 'districtIndex', 'current'),
        (d) => setIndexDoc(d.exists() ? d.data() : null),
        onErr
      ),
      onSnapshot(
        doc(db, 'apiUsage', `foursquare_${CURRENT_MONTH}`),
        (d) => setUsageDoc(d.exists() ? d.data() : null),
        onErr
      ),
    ];
    return () => unsubs.forEach((u) => u());
  }, [showError]);

  const stats = useMemo(() => {
    const byTier = { trial: 0, starter: 0, pro: 0, enterprise: 0 };
    let live = 0;
    let withPlaceId = 0;
    venues.forEach((v) => {
      if (v.published === true) live += 1;
      if (v.placeId) withPlaceId += 1;
      const t = v.subscriptionTier || 'trial';
      if (byTier[t] !== undefined) byTier[t] += 1;
    });
    return {
      total: venues.length,
      live,
      withPlaceId,
      byTier,
      subscribed: byTier.starter + byTier.pro + byTier.enterprise,
      owners: users.length,
      totalDistricts: districtDocs.length,
      activeDistricts: districtDocs.filter((d) => d.active === true).length,
    };
  }, [venues, users, districtDocs]);

  const districtCache = useMemo(
    () =>
      cacheDocs
        .map((d) => ({
          id: d.id,
          name: d.name || d.id,
          venueCount: Array.isArray(d.venues) ? d.venues.length : 0,
          fetchedAt: d.fetchedAt,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [cacheDocs]
  );

  const usage = {
    total: usageDoc?.totalCalls || 0,
    refresh: usageDoc?.refreshCalls || 0,
    search: usageDoc?.searchCalls || 0,
    // Absent on documents written before the Pro/Premium split was tracked,
    // which correctly reads as "all of it was Pro-billed".
    premium: usageDoc?.premiumCalls || 0,
  };

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/');
  }, [navigate]);

  return (
    // Standalone route — the admin console renders outside DashboardLayout,
    // so this shell owns the page's <main>.
    <PageShell as="main" rings={RING_SETS.panel} width="max-w-[1400px]">
      <PageHeading
        eyebrow="Internal"
        title="Admin Console"
        description="Live platform telemetry: venue and owner directories, Foursquare quota, Paystack revenue, and Pro voucher codes."
        actions={
          <>
            <span className="inline-flex items-center gap-1.5 font-mono text-sm text-white/60">
              <span className="h-2 w-2 rounded-full bg-brand-green" />
              Live
            </span>
            <button
              type="button"
              onClick={handleLogout}
              className={brandButton('outline', 'sm')}
            >
              <ArrowRightOnRectangleIcon
                className="h-4 w-4"
                aria-hidden="true"
              />
              Log Out
            </button>
          </>
        }
      />

      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-1">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-lg px-4 py-2.5 font-mono text-sm transition ${
                tab === t
                  ? 'bg-white/10 font-bold text-white'
                  : 'text-white/55 hover:text-white'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <SegmentedRule variant="cool" />
      </div>

      {!ready && LIVE_DATA_TABS.includes(tab) ? (
        <div className="flex items-center justify-center py-24">
          <BrandSpinner />
        </div>
      ) : (
        <>
          {tab === 'Overview' && (
            <OverviewTab
              stats={stats}
              usage={usage}
              districtCache={districtCache}
              indexUpdatedAt={indexDoc?.updatedAt}
            />
          )}
          {tab === 'Venues & Owners' && (
            <DirectoryTab venues={venues} users={users} />
          )}
          {tab === 'Revenue' && <RevenueTab />}
          {tab === 'Vouchers' && <VouchersTab />}
        </>
      )}
    </PageShell>
  );
}

export default AdminDashboard;
